import apiClient from './apiClient';
import { API_URL } from '../config';
import type { GradingData } from './transcriptionService';

export interface ReportResponse {
  id: number;
  session_id: string;
  template_id: string;
  report_text: string;
  diagnosis_category: string | null;
  pathology_type: string | null;
  cpt_code: string | null;
  cpt_exam_name: string | null;
  cpt_laterality: string | null;
  cpt_contrast: string | null;
  cpt_detection_method: string | null;
  cpt_detection_confidence: string | null;
  status: string;
  finalized_at: string | null;
  finalized_by: number | null;
  last_edited_at: string | null;
  last_edited_by: number | null;
  created_at: string;
  grading_data?: GradingData | null;
}

export interface PatientContextPayload {
  first_name?: string;
  last_name?: string;
  mrn?: string;
  dob?: string;
  gender?: string;
  accession_number?: string;
  modality?: string;
  study_date?: string;
  body_part?: string;
}

export interface GenerateReportRequest {
  session_id: string;
  clinical_history?: string;
  patient_context?: PatientContextPayload;
  cpt_code?: string;
  cpt_exam_name?: string;  // For disambiguating CPT code lookups
  cpt_detection_method?: string;  // "manual", "rule_based", "llm_detected", "fallback"
  cpt_modality?: string;  // e.g. "CT", "MRI" — from LLM detection metadata
  cpt_body_region?: string;  // e.g. "Abdomen" — from LLM detection metadata
  cpt_laterality?: string;  // e.g. "left", "right", "bilateral"
  cpt_contrast?: string;  // e.g. "without_contrast", "with_contrast"
  cpt_detection_confidence?: string;  // LLM confidence score as string
  custom_template?: string; // Custom template structure for report generation
}

export interface UpdateReportRequest {
  sections: Record<string, string>;
  status?: string;
}

const generateReport = async (data: GenerateReportRequest): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/generate`, data);
  return response.data;
};

const getReport = async (sessionId: string): Promise<ReportResponse> => {
  const response = await apiClient.get(`${API_URL}/reports/${sessionId}`);
  return response.data;
};

const regenerateReport = async (data: GenerateReportRequest): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/regenerate`, data);
  return response.data;
};

const updateReport = async (reportId: number, data: UpdateReportRequest): Promise<ReportResponse> => {
  const response = await apiClient.put(`${API_URL}/reports/${reportId}`, data);
  return response.data;
};

const finalizeReport = async (reportId: number): Promise<ReportResponse> => {
  const response = await apiClient.post(`${API_URL}/reports/${reportId}/finalize`);
  return response.data;
};

const exportReportHTML = async (reportId: number, sessionId: string): Promise<void> => {
  const tzOffset = -new Date().getTimezoneOffset();
  const response = await apiClient.get(`${API_URL}/reports/${reportId}/export/html`, {
    params: { tz_offset: tzOffset },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], { type: 'text/html' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `report_${reportId}_${sessionId}.html`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const exportReportDOCX = async (reportId: number, sessionId: string): Promise<void> => {
  const tzOffset = -new Date().getTimezoneOffset();
  const response = await apiClient.get(`${API_URL}/reports/${reportId}/export/docx`, {
    params: { tz_offset: tzOffset },
    responseType: 'blob',
  });
  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `report_${reportId}_${sessionId}.docx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const reportService = {
  generateReport,
  getReport,
  regenerateReport,
  updateReport,
  finalizeReport,
  exportReportHTML,
  exportReportDOCX,
};

export default reportService;
